// Shared form-rendering primitives. Imported by both:
//   - admin/app.ts        (the full /admin/ multi-pane editor)
//   - admin/inline-edit.ts (click-to-edit modal on the public site)
//
// Pure functions: no module-level mutable state except the image picker cache
// (which is keyed by the shared backend, so safe to share globally).
import type {
  Dj, Embed, ProfileBody,
  EventItem, RsvpButton, GalleryImage, TimetableRow,
  HomeContent, FeaturedSet, Imprint,
  PagesContent,
  Release, ReleasePlatform, Post,
} from "../types.ts";

// ---- DOM helpers ----

export function $<T extends Element = HTMLElement>(s: string, root: ParentNode = document): T | null {
  return root.querySelector<T>(s);
}
export function $req<T extends Element = HTMLElement>(s: string, root: ParentNode = document): T {
  const el = root.querySelector<T>(s);
  if (!el) throw new Error(`element not found: ${s}`);
  return el;
}
export function $$<T extends Element = HTMLElement>(s: string, root: ParentNode = document): T[] {
  return Array.from(root.querySelectorAll<T>(s));
}

export type AttrValue = string | number | boolean | null | undefined | EventListener;
export type Attrs = Record<string, AttrValue>;
export type Child = string | Node | null | false | undefined;

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs, ...children: (Child | Child[])[]): HTMLElementTagNameMap[K];
export function el(tag: string, attrs?: Attrs, ...children: (Child | Child[])[]): HTMLElement;
export function el(tag: string, attrs: Attrs = {}, ...children: (Child | Child[])[]): HTMLElement {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class" && typeof v === "string") e.className = v;
    else if (k === "html" && typeof v === "string") e.innerHTML = v;
    else if (k.startsWith("on") && typeof v === "function") {
      e.addEventListener(k.slice(2).toLowerCase(), v);
    } else if (v === false || v == null) {
      continue;
    } else if (v === true) {
      e.setAttribute(k, "");
    } else {
      e.setAttribute(k, String(v));
    }
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    e.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return e;
}

// ---- Form widgets ----

export interface InputOpts {
  type?: string;
  placeholder?: string;
  rows?: number;
}

export function field(label: string, input: Node, help?: string): HTMLElement {
  return el("div", { class: "field" },
    el("label", {}, label),
    help ? el("div", { class: "help" }, help) : null,
    input
  );
}

export function textInput(value: string | undefined, onInput: (v: string) => void, opts: InputOpts = {}): HTMLInputElement {
  return el("input", {
    type: opts.type ?? "text",
    value: value ?? "",
    placeholder: opts.placeholder ?? "",
    onInput: (e: Event) => onInput((e.target as HTMLInputElement).value),
  }) as HTMLInputElement;
}

export function textarea(value: string | undefined, onInput: (v: string) => void, opts: InputOpts = {}): HTMLTextAreaElement {
  return el("textarea", {
    rows: opts.rows ?? 4,
    placeholder: opts.placeholder ?? "",
    onInput: (e: Event) => onInput((e.target as HTMLTextAreaElement).value),
  }, value ?? "") as HTMLTextAreaElement;
}

export function tagInput(values: string[], onChange: (v: string[]) => void): HTMLElement {
  const list = el("div", { class: "tag-list" });
  function render(): void {
    list.innerHTML = "";
    values.forEach((t, i) => {
      list.appendChild(el("span", { class: "tag" }, t,
        el("span", {
          class: "x", title: "Remove",
          onClick: () => { values.splice(i, 1); render(); onChange(values); }
        }, "×")
      ));
    });
    const input = el("input", { type: "text", placeholder: "+ add tag", style: "width:140px" }) as HTMLInputElement;
    input.addEventListener("keydown", e => {
      if (e.key === "Enter" && input.value.trim()) {
        e.preventDefault();
        values.push(input.value.trim());
        input.value = "";
        render();
        onChange(values);
      }
    });
    list.appendChild(input);
  }
  render();
  return list;
}

// ---- Image picker ----

interface ImageEntry { path: string; dir: string; size: number; mtime: number; }
interface ImageList { files: ImageEntry[]; dirs: string[]; fetchedAt?: number; }

let imagePickerCache: ImageList | null = null;

export async function refreshImagePickerCache(): Promise<void> {
  const r = await fetch("/admin/api/images", { credentials: "same-origin" });
  if (!r.ok) throw new Error("failed to list images");
  imagePickerCache = await r.json() as ImageList;
  imagePickerCache.fetchedAt = Date.now();
}

export function imageInput(value: string | undefined, onChange: (v: string) => void, defaultDir: string = "hero"): HTMLElement {
  const wrap = el("div", { class: "imgctl" });
  const thumb = el("div", { class: "thumb", title: "Click to browse" });
  const input = el("input", { type: "text", value: value ?? "", placeholder: "/assets/img/…" }) as HTMLInputElement;
  const browse = el("button", { type: "button", class: "btn ghost", title: "Browse / upload" }, "Browse");
  const clear = el("button", { type: "button", class: "clear", title: "Clear" }, "×");
  function refresh(): void {
    if (input.value) {
      thumb.style.backgroundImage = `url('${input.value.replace(/'/g, "\\'")}')`;
      thumb.textContent = "";
    } else {
      thumb.style.backgroundImage = "";
      thumb.textContent = "no image";
    }
  }
  input.addEventListener("input", () => { onChange(input.value); refresh(); });
  thumb.addEventListener("click", () => browse.click());
  browse.addEventListener("click", () => {
    openImagePicker(defaultDir, picked => {
      input.value = picked;
      onChange(picked);
      refresh();
    });
  });
  clear.addEventListener("click", () => {
    input.value = "";
    onChange("");
    refresh();
  });
  refresh();
  wrap.appendChild(thumb);
  wrap.appendChild(el("div", { class: "right" },
    el("div", { class: "row" }, input, browse, clear)
  ));
  return wrap;
}

export function openImagePicker(defaultDir: string, onPick: (path: string) => void): void {
  const back = el("div", { class: "imgpicker-back", role: "dialog", "aria-modal": "true" });
  const modal = el("div", { class: "imgpicker" });
  const header = el("header", {});
  const grid = el("div", { class: "grid" });
  const footer = el("footer", {});

  let currentDir = defaultDir || "hero";
  const upStatus = el("span", { class: "upstatus" });

  function close(): void { document.body.removeChild(back); }
  back.addEventListener("click", e => { if (e.target === back) close(); });
  const escHandler = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      close();
      document.removeEventListener("keydown", escHandler);
    }
  };
  document.addEventListener("keydown", escHandler);

  function render(): void {
    grid.innerHTML = "";
    if (!imagePickerCache) {
      grid.appendChild(el("div", { class: "empty" }, "Loading…"));
      return;
    }
    const filtered = imagePickerCache.files.filter(f => currentDir === "" || f.dir === currentDir);
    if (!filtered.length) {
      grid.appendChild(el("div", { class: "empty" }, `No images in /${currentDir || "(root)"}/. Upload one below.`));
      return;
    }
    filtered.forEach(f => {
      const tile = el("div", { class: "tile", title: f.path });
      const img = el("img", { src: f.path, alt: f.path, loading: "lazy" });
      const name = el("div", { class: "name" }, f.path.split("/").pop() ?? "");
      const del = el("button", {
        type: "button", class: "del", title: "Delete this image",
        onClick: async (ev: Event) => {
          ev.stopPropagation();
          if (!confirm(`Delete ${f.path}?\n\nThis is permanent. Pages still referencing it will show a broken image.`)) return;
          const r = await fetch(`/admin/api/images?path=${encodeURIComponent(f.path)}`, { method: "DELETE", credentials: "same-origin" });
          if (!r.ok) { alert("Delete failed: " + await r.text()); return; }
          await refreshImagePickerCache();
          renderHeader(); render();
        }
      }, "×");
      tile.appendChild(del);
      tile.appendChild(img);
      tile.appendChild(name);
      tile.addEventListener("click", () => { onPick(f.path); close(); });
      grid.appendChild(tile);
    });
  }

  function renderHeader(): void {
    header.innerHTML = "";
    header.appendChild(el("div", { class: "title" }, "Images"));
    const sel = el("select", {
      onChange: (e: Event) => { currentDir = (e.target as HTMLSelectElement).value; render(); }
    }) as HTMLSelectElement;
    (imagePickerCache?.dirs ?? [defaultDir]).forEach(d => {
      sel.appendChild(el("option", { value: d }, d || "(all)"));
    });
    sel.value = currentDir;
    header.appendChild(sel);
    header.appendChild(el("div", { class: "spacer" }));
    header.appendChild(el("button", { type: "button", class: "btn ghost", onClick: close }, "Close"));
  }

  function renderFooter(): void {
    footer.innerHTML = "";
    footer.appendChild(el("label", {}, "Upload to:"));
    const dirInput = el("input", { type: "text", value: currentDir, placeholder: "subfolder under /assets/img/", style: "width:240px" }) as HTMLInputElement;
    footer.appendChild(dirInput);
    const fileInput = el("input", { type: "file", accept: ".jpg,.jpeg,.png,.webp,.svg,.gif,.avif" }) as HTMLInputElement;
    footer.appendChild(fileInput);
    const upBtn = el("button", {
      type: "button", class: "btn primary",
      onClick: async () => {
        if (!fileInput.files || !fileInput.files[0]) { alert("Pick a file first"); return; }
        const fd = new FormData();
        fd.append("dir", dirInput.value);
        fd.append("file", fileInput.files[0]);
        upStatus.textContent = "uploading…";
        const r = await fetch("/admin/api/images/upload", { method: "POST", body: fd, credentials: "same-origin" });
        if (!r.ok) { upStatus.textContent = "upload failed: " + await r.text(); return; }
        const j = await r.json() as { ok: boolean; path: string; size: number };
        upStatus.textContent = `uploaded ${j.path}`;
        await refreshImagePickerCache();
        currentDir = dirInput.value;
        renderHeader(); render();
        fileInput.value = "";
      }
    }, "Upload");
    footer.appendChild(upBtn);
    footer.appendChild(upStatus);
  }

  modal.appendChild(header); modal.appendChild(grid); modal.appendChild(footer);
  back.appendChild(modal);
  document.body.appendChild(back);

  void refreshImagePickerCache().then(() => { renderHeader(); render(); renderFooter(); });
  renderHeader(); render(); renderFooter();
}

// ---- DJ factory ----

/** Build a fresh "stub" DJ with the given slug. Used by both the admin UI and
 * the inline-edit overlay's Add flow so defaults stay in lockstep. */
export function createNewDj(slug: string): Dj {
  return {
    slug,
    name: slug,
    tagline: "Profile in incubation. Bio + links landing soon.",
    tags: [],
    external_url: null,
    external_label: "SoundCloud",
    page_meta: { description: `${slug} profile page for Breathe Music Records.` },
    in_roster_index: false,
    embed: null,
    profile_body: {
      type: "stub",
      placeholder_title: "What we’ll add here",
      placeholder_text: "Short bio • Full bio • Featured embed • Style tags • Links.",
    },
    hero_image: "",
    hero_image_pos: "center",
    hero_image_opacity: "1",
    gallery: [],
  };
}

// ---- DJ form ----

export interface DjFormOpts {
  /** Called whenever any field of `dj` mutates. Caller uses this to track dirty state. */
  onChange: () => void;
  /** If provided AND showDelete !== false, a Delete button appears in the header. */
  onDelete?: () => void;
  /** Show the Delete button in the header. Defaults to true when onDelete is provided. */
  showDelete?: boolean;
  /** Show a subtitle/header. Defaults to dj.name. Set "" to suppress. */
  heading?: string;
}

export function renderDjForm(dj: Dj, root: HTMLElement, opts: DjFormOpts): void {
  const onChange = opts.onChange;
  const showHeading = opts.heading !== "";
  const showDelete = opts.showDelete ?? !!opts.onDelete;
  root.innerHTML = "";

  if (showHeading) {
    const h = el("h3", {}, opts.heading ?? dj.name ?? "(untitled)");
    if (showDelete && opts.onDelete) {
      const onDelete = opts.onDelete;
      h.appendChild(el("button", {
        type: "button", class: "btn ghost danger", style: "float:right;font-size:12px",
        onClick: () => {
          if (!confirm(`Delete ${dj.name}? The page file remains until you regenerate.`)) return;
          onDelete();
        }
      }, "Delete"));
    }
    root.appendChild(h);
  }

  root.appendChild(field("Name", textInput(dj.name, v => { dj.name = v; onChange(); })));
  root.appendChild(field("Slug (URL path)", textInput(dj.slug, v => { dj.slug = v; onChange(); }), "Lowercase, hyphens. Changes URL: /roster/<slug>/"));
  root.appendChild(field("Tagline", textInput(dj.tagline, v => { dj.tagline = v; onChange(); })));
  root.appendChild(field("Hero tags", tagInput(dj.tags, v => { dj.tags = v; onChange(); }), "Press Enter to add."));

  // Hero background image (optional)
  const hero = el("fieldset", { class: "fieldset" }, el("legend", {}, "Hero background"));
  hero.appendChild(field(
    "Hero image",
    imageInput(dj.hero_image ?? "", v => { dj.hero_image = v; onChange(); }, `roster/${dj.slug}`),
    "Optional. Sits behind the kicker/title/tagline. Leave empty for the default flat hero."
  ));
  hero.appendChild(field(
    "Image position",
    textInput(dj.hero_image_pos ?? "center", v => { dj.hero_image_pos = v; onChange(); }, { placeholder: "center" }),
    "CSS background-position. e.g. ‘center’, ‘center top’, ‘50% 30%’."
  ));
  hero.appendChild(field(
    "Image opacity",
    textInput(dj.hero_image_opacity ?? "1", v => { dj.hero_image_opacity = v; onChange(); }, { placeholder: "1" }),
    "0–1. Lower fades the image into the background."
  ));
  root.appendChild(hero);

  root.appendChild(field("Page meta description", textarea(dj.page_meta?.description ?? "", v => {
    dj.page_meta = dj.page_meta ?? { description: "" };
    dj.page_meta.description = v;
    onChange();
  }, { rows: 2 }), "Search engine snippet."));

  root.appendChild(field("External link URL", textInput(dj.external_url ?? "", v => { dj.external_url = v || null; onChange(); }, { type: "url" }),
    "Leave blank to render a disabled ‘link coming’ button."));
  root.appendChild(field("External link label", textInput(dj.external_label, v => { dj.external_label = v; onChange(); })));

  // Index card
  const idx = el("fieldset", { class: "fieldset" }, el("legend", {}, "Roster index card"));
  const inIdx = el("input", { type: "checkbox" }) as HTMLInputElement;
  inIdx.checked = !!dj.in_roster_index;
  inIdx.addEventListener("change", () => {
    dj.in_roster_index = inIdx.checked;
    onChange();
    renderDjForm(dj, root, opts);
  });
  idx.appendChild(field("Show in /roster/ index", inIdx));
  if (dj.in_roster_index) {
    dj.index_card = dj.index_card ?? { tags: [], description: "" };
    const card = dj.index_card;
    idx.appendChild(field("Card tags", tagInput(card.tags, v => { card.tags = v; onChange(); })));
    idx.appendChild(field("Card description", textarea(card.description, v => { card.description = v; onChange(); }, { rows: 2 })));
  }
  root.appendChild(idx);

  // Profile body type
  const body = el("fieldset", { class: "fieldset" }, el("legend", {}, "Profile body"));
  const typeSel = el("select", {
    onChange: (e: Event) => {
      const newType = (e.target as HTMLSelectElement).value as ProfileBody["type"];
      if (newType === "full") {
        dj.profile_body = {
          type: "full",
          short_bio_html: dj.profile_body.type === "full" ? dj.profile_body.short_bio_html : "",
          full_bio_html: dj.profile_body.type === "full" ? dj.profile_body.full_bio_html : "",
          sonic_signature: dj.profile_body.type === "full" ? dj.profile_body.sonic_signature : { vibe: "", technique: "", current_chapter: "" },
          note_card: dj.profile_body.type === "full" ? dj.profile_body.note_card : { title: "Note", body: "" },
        };
      } else if (newType === "minimal") {
        dj.profile_body = {
          type: "minimal",
          notes_meta: dj.profile_body.type === "minimal" ? dj.profile_body.notes_meta : "Profile details landing soon.",
          notes_body: dj.profile_body.type === "minimal" ? dj.profile_body.notes_body : "Bio coming soon. For now, the music speaks.",
        };
      } else {
        dj.profile_body = {
          type: "stub",
          placeholder_title: "What we’ll add here",
          placeholder_text: "Short bio • Full bio • Featured embed • Style tags • Links.",
        };
      }
      onChange();
      renderDjForm(dj, root, opts);
    }
  },
    el("option", { value: "full" }, "Full (bio + sonic signature)"),
    el("option", { value: "minimal" }, "Minimal (embed + notes)"),
    el("option", { value: "stub" }, "Stub (placeholder)")
  ) as HTMLSelectElement;
  typeSel.value = dj.profile_body.type;
  body.appendChild(field("Body type", typeSel));

  if (dj.profile_body.type !== "stub") {
    dj.embed = dj.embed ?? { platform: "soundcloud", url: "", title: "Featured mix", meta: "" };
    const embed: Embed = dj.embed;
    const platSel = el("select", {
      onChange: (e: Event) => {
        embed.platform = (e.target as HTMLSelectElement).value as Embed["platform"];
        onChange();
        renderDjForm(dj, root, opts);
      }
    },
      el("option", { value: "soundcloud" }, "SoundCloud"),
      el("option", { value: "mixcloud" }, "Mixcloud")
    ) as HTMLSelectElement;
    platSel.value = embed.platform;
    body.appendChild(field("Embed platform", platSel));
    body.appendChild(field("Embed URL", textInput(embed.url ?? "", v => { embed.url = v; onChange(); }, { type: "url" })));
    if (embed.platform === "mixcloud") {
      body.appendChild(field("Mixcloud feed (e.g. /user/mix/)", textInput(embed.feed ?? "", v => { embed.feed = v; onChange(); }),
        "Path portion only. URL field is for the click-through link."));
    }
    body.appendChild(field("Embed card title", textInput(embed.title || "Featured mix", v => { embed.title = v; onChange(); })));
    body.appendChild(field("Embed card meta", textInput(embed.meta ?? "", v => { embed.meta = v; onChange(); })));
  }

  if (dj.profile_body.type === "full") {
    const pb = dj.profile_body;
    body.appendChild(field("Short bio (HTML)", textarea(pb.short_bio_html, v => { pb.short_bio_html = v; onChange(); }, { rows: 6 }),
      "Wrap paragraphs in <p>…</p>."));
    body.appendChild(field("Full story (HTML)", textarea(pb.full_bio_html, v => { pb.full_bio_html = v; onChange(); }, { rows: 12 }),
      "<h3>Section</h3><p>…</p> structure."));
    body.appendChild(field("Sonic signature · Vibe", textInput(pb.sonic_signature.vibe, v => { pb.sonic_signature.vibe = v; onChange(); })));
    body.appendChild(field("Sonic signature · Technique", textInput(pb.sonic_signature.technique, v => { pb.sonic_signature.technique = v; onChange(); })));
    body.appendChild(field("Sonic signature · Current chapter", textInput(pb.sonic_signature.current_chapter, v => { pb.sonic_signature.current_chapter = v; onChange(); })));
    body.appendChild(field("Note card title", textInput(pb.note_card.title, v => { pb.note_card.title = v; onChange(); })));
    body.appendChild(field("Note card body", textarea(pb.note_card.body, v => { pb.note_card.body = v; onChange(); }, { rows: 3 })));
  } else if (dj.profile_body.type === "minimal") {
    const pb = dj.profile_body;
    body.appendChild(field("Notes panel meta", textInput(pb.notes_meta, v => { pb.notes_meta = v; onChange(); })));
    body.appendChild(field("Notes panel body", textarea(pb.notes_body, v => { pb.notes_body = v; onChange(); }, { rows: 3 })));
  } else {
    const pb = dj.profile_body;
    body.appendChild(field("Placeholder title", textInput(pb.placeholder_title, v => { pb.placeholder_title = v; onChange(); })));
    body.appendChild(field("Placeholder text", textarea(pb.placeholder_text, v => { pb.placeholder_text = v; onChange(); }, { rows: 2 })));
  }
  root.appendChild(body);

  // Gallery (optional, rendered at bottom of profile)
  const gal = el("fieldset", { class: "fieldset" }, el("legend", {}, "Gallery"));
  const galRoot = el("div");
  function renderGal(): void {
    galRoot.innerHTML = "";
    const list: GalleryImage[] = dj.gallery ?? (dj.gallery = []);
    list.forEach((g: GalleryImage, i: number) => {
      const row = el("div", { style: "display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;align-items:start" });
      row.appendChild(imageInput(g.src, v => { g.src = v; onChange(); }, `roster/${dj.slug}`));
      row.appendChild(el("div", { style: "display:flex;gap:6px" },
        el("input", { type: "text", value: g.alt, placeholder: "Alt text", onInput: (e: Event) => { g.alt = (e.target as HTMLInputElement).value; onChange(); } }),
        el("button", { type: "button", class: "btn ghost danger", onClick: () => { list.splice(i, 1); onChange(); renderGal(); } }, "×")
      ));
      galRoot.appendChild(row);
    });
    galRoot.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { list.push({ src: "", alt: "" }); onChange(); renderGal(); } }, "+ Add image"));
  }
  renderGal();
  gal.appendChild(galRoot);
  root.appendChild(gal);
}

// ---- Event factory ----

export function createNewEvent(slug: string): EventItem {
  return {
    slug,
    title: slug,
    kicker: "Breathe Music Records Presents",
    date_display: "TBA",
    time_display: "",
    doors_display: "",
    lede: "",
    location_meta: "",
    location_body_html: "",
    rsvp_buttons: [],
    rsvp_buttons_location: [],
    timetable: [],
    what_to_expect: "",
    bring: "",
    flyer_image: "",
    gallery: [],
    page_meta: { description: "", og_title: "", og_description: "", og_image: "" },
  };
}

// ---- Event form ----

export interface EventFormOpts {
  onChange: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
  heading?: string;
}

export function renderEventForm(ev: EventItem, root: HTMLElement, opts: EventFormOpts): void {
  const onChange = opts.onChange;
  const showHeading = opts.heading !== "";
  const showDelete = opts.showDelete ?? !!opts.onDelete;
  root.innerHTML = "";

  if (showHeading) {
    const h = el("h3", {}, opts.heading ?? ev.title ?? "(untitled)");
    if (showDelete && opts.onDelete) {
      const onDelete = opts.onDelete;
      h.appendChild(el("button", {
        type: "button", class: "btn ghost danger", style: "float:right;font-size:12px",
        onClick: () => {
          if (!confirm(`Delete ${ev.title}?`)) return;
          onDelete();
        }
      }, "Delete"));
    }
    root.appendChild(h);
  }

  root.appendChild(field("Title", textInput(ev.title, v => { ev.title = v; onChange(); })));
  root.appendChild(field("Slug (URL)", textInput(ev.slug, v => { ev.slug = v; onChange(); })));
  root.appendChild(field("Kicker", textInput(ev.kicker, v => { ev.kicker = v; onChange(); })));
  root.appendChild(field("Date display", textInput(ev.date_display, v => { ev.date_display = v; onChange(); })));
  root.appendChild(field("Time display", textInput(ev.time_display, v => { ev.time_display = v; onChange(); })));
  root.appendChild(field("Doors display", textInput(ev.doors_display, v => { ev.doors_display = v; onChange(); })));
  root.appendChild(field("Lede", textarea(ev.lede, v => { ev.lede = v; onChange(); }, { rows: 3 })));
  root.appendChild(field("Flyer image", imageInput(ev.flyer_image, v => { ev.flyer_image = v; onChange(); }, `events/${ev.slug}`), "Click thumbnail or Browse to upload."));

  const tt = el("fieldset", { class: "fieldset" }, el("legend", {}, "Timetable"));
  const ttRoot = el("div");
  function renderTT(): void {
    ttRoot.innerHTML = "";
    ev.timetable.forEach((row: TimetableRow, i: number) => {
      ttRoot.appendChild(el("div", { class: "row2", style: "margin-bottom:6px" },
        el("input", { type: "text", value: row.time, placeholder: "HH:MM–HH:MM", onInput: (e: Event) => { row.time = (e.target as HTMLInputElement).value; onChange(); } }),
        el("div", { style: "display:flex;gap:6px" },
          el("input", { type: "text", value: row.act, placeholder: "Act name", onInput: (e: Event) => { row.act = (e.target as HTMLInputElement).value; onChange(); } }),
          el("button", { type: "button", class: "btn ghost danger", onClick: () => { ev.timetable.splice(i, 1); onChange(); renderTT(); } }, "×")
        )
      ));
    });
    ttRoot.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { ev.timetable.push({ time: "", act: "" }); onChange(); renderTT(); } }, "+ Add row"));
  }
  renderTT();
  tt.appendChild(ttRoot);
  root.appendChild(tt);

  root.appendChild(field("Location meta (heading)", textInput(ev.location_meta, v => { ev.location_meta = v; onChange(); })));
  root.appendChild(field("Location body (HTML)", textarea(ev.location_body_html, v => { ev.location_body_html = v; onChange(); }, { rows: 4 })));
  root.appendChild(field("What to expect", textarea(ev.what_to_expect, v => { ev.what_to_expect = v; onChange(); }, { rows: 3 })));
  root.appendChild(field("Bring", textarea(ev.bring, v => { ev.bring = v; onChange(); }, { rows: 2 })));

  function renderRsvp(arr: RsvpButton[], holder: HTMLElement): void {
    holder.innerHTML = "";
    arr.forEach((b: RsvpButton, i: number) => {
      const checkbox = el("input", { type: "checkbox", title: "Primary?" }) as HTMLInputElement;
      checkbox.checked = !!b.primary;
      checkbox.addEventListener("change", () => { b.primary = checkbox.checked; onChange(); });
      holder.appendChild(el("div", { class: "row2", style: "margin-bottom:6px" },
        el("input", { type: "text", value: b.label, placeholder: "Label", onInput: (e: Event) => { b.label = (e.target as HTMLInputElement).value; onChange(); } }),
        el("div", { style: "display:flex;gap:6px;align-items:center" },
          el("input", { type: "text", value: b.url, placeholder: "URL", onInput: (e: Event) => { b.url = (e.target as HTMLInputElement).value; onChange(); } }),
          checkbox,
          el("button", { type: "button", class: "btn ghost danger", onClick: () => { arr.splice(i, 1); onChange(); renderRsvp(arr, holder); } }, "×")
        )
      ));
    });
    holder.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { arr.push({ label: "", url: "", primary: false }); onChange(); renderRsvp(arr, holder); } }, "+ Add button"));
  }

  const rsvp = el("fieldset", { class: "fieldset" }, el("legend", {}, "RSVP buttons (intro section)"));
  const rsvpRoot = el("div");
  renderRsvp(ev.rsvp_buttons, rsvpRoot);
  rsvp.appendChild(rsvpRoot);
  root.appendChild(rsvp);

  const rsvp2 = el("fieldset", { class: "fieldset" }, el("legend", {}, "RSVP buttons (location card)"));
  const rsvp2Root = el("div");
  renderRsvp(ev.rsvp_buttons_location, rsvp2Root);
  rsvp2.appendChild(rsvp2Root);
  root.appendChild(rsvp2);

  // Gallery
  const gal = el("fieldset", { class: "fieldset" }, el("legend", {}, "Gallery"));
  const galRoot = el("div");
  function renderGal(): void {
    galRoot.innerHTML = "";
    ev.gallery.forEach((g: GalleryImage, i: number) => {
      const row = el("div", { style: "display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;align-items:start" });
      row.appendChild(imageInput(g.src, v => { g.src = v; onChange(); }, `events/${ev.slug}`));
      row.appendChild(el("div", { style: "display:flex;gap:6px" },
        el("input", { type: "text", value: g.alt, placeholder: "Alt text", onInput: (e: Event) => { g.alt = (e.target as HTMLInputElement).value; onChange(); } }),
        el("button", { type: "button", class: "btn ghost danger", onClick: () => { ev.gallery.splice(i, 1); onChange(); renderGal(); } }, "×")
      ));
      galRoot.appendChild(row);
    });
    galRoot.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { ev.gallery.push({ src: "", alt: "" }); onChange(); renderGal(); } }, "+ Add image"));
  }
  renderGal();
  gal.appendChild(galRoot);
  root.appendChild(gal);

  // Meta
  const meta = el("fieldset", { class: "fieldset" }, el("legend", {}, "SEO / OpenGraph"));
  meta.appendChild(field("Page meta description", textarea(ev.page_meta.description, v => { ev.page_meta.description = v; onChange(); }, { rows: 2 })));
  meta.appendChild(field("og:title", textInput(ev.page_meta.og_title ?? "", v => { ev.page_meta.og_title = v; onChange(); })));
  meta.appendChild(field("og:description", textInput(ev.page_meta.og_description ?? "", v => { ev.page_meta.og_description = v; onChange(); })));
  meta.appendChild(field("og:image", imageInput(ev.page_meta.og_image ?? "", v => { ev.page_meta.og_image = v; onChange(); }, `events/${ev.slug}`)));
  root.appendChild(meta);
}

// ---- Home form ----

export interface HomeFormOpts {
  onChange: () => void;
  heading?: string;
}

export function renderHomeForm(h: HomeContent, root: HTMLElement, opts: HomeFormOpts): void {
  const onChange = opts.onChange;
  root.innerHTML = "";
  if (opts.heading !== "") root.appendChild(el("h3", {}, opts.heading ?? "Edit home page"));

  const seo = el("fieldset", { class: "fieldset" }, el("legend", {}, "Page meta"));
  seo.appendChild(field("<title>", textInput(h.page_meta.title, v => { h.page_meta.title = v; onChange(); })));
  seo.appendChild(field("Meta description", textarea(h.page_meta.description, v => { h.page_meta.description = v; onChange(); }, { rows: 2 })));
  root.appendChild(seo);

  const hero = el("fieldset", { class: "fieldset" }, el("legend", {}, "Hero"));
  hero.appendChild(field("Kicker", textInput(h.hero_kicker, v => { h.hero_kicker = v; onChange(); })));
  hero.appendChild(field("Title", textInput(h.hero_title, v => { h.hero_title = v; onChange(); })));
  hero.appendChild(field("Lede", textarea(h.hero_lede, v => { h.hero_lede = v; onChange(); }, { rows: 2 })));
  hero.appendChild(field("Hero badges", tagInput(h.hero_badges, v => { h.hero_badges = v; onChange(); })));
  const carRoot = el("div");
  function renderCar(): void {
    carRoot.innerHTML = "";
    h.hero_carousel.forEach((src: string, i: number) => {
      const row = el("div", { style: "display:flex;gap:6px;margin-bottom:6px;align-items:start" });
      const ic = imageInput(src, v => { h.hero_carousel[i] = v; onChange(); }, "hero");
      ic.style.flex = "1";
      row.appendChild(ic);
      row.appendChild(el("button", { type: "button", class: "btn ghost danger", onClick: () => { h.hero_carousel.splice(i, 1); onChange(); renderCar(); } }, "×"));
      carRoot.appendChild(row);
    });
    carRoot.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { h.hero_carousel.push(""); onChange(); renderCar(); } }, "+ Add slide"));
  }
  renderCar();
  hero.appendChild(field("Hero carousel slides", carRoot));
  root.appendChild(hero);

  const arch = el("fieldset", { class: "fieldset" }, el("legend", {}, "Architecture intro"));
  arch.appendChild(field("Kicker", textInput(h.architecture.kicker, v => { h.architecture.kicker = v; onChange(); })));
  arch.appendChild(field("Title", textInput(h.architecture.title, v => { h.architecture.title = v; onChange(); })));
  arch.appendChild(field("Lede", textarea(h.architecture.lede, v => { h.architecture.lede = v; onChange(); }, { rows: 2 })));
  root.appendChild(arch);

  const imp = el("fieldset", { class: "fieldset" }, el("legend", {}, "Imprints"));
  const impRoot = el("div");
  function renderImp(): void {
    impRoot.innerHTML = "";
    h.imprints.forEach((m: Imprint, i: number) => {
      const card = el("div", { style: "border:1px solid var(--border);padding:10px;margin-bottom:8px;border-radius:5px" });
      card.appendChild(field("Label (A/B/C)", textInput(m.label, v => { m.label = v; onChange(); })));
      card.appendChild(field("Name", textInput(m.name, v => { m.name = v; onChange(); })));
      card.appendChild(field("Description", textInput(m.description, v => { m.description = v; onChange(); })));
      card.appendChild(field("Background image", imageInput(m.background, v => { m.background = v; onChange(); }, "hero")));
      card.appendChild(field("Pills", tagInput(m.pills, v => { m.pills = v; onChange(); })));
      card.appendChild(el("button", { type: "button", class: "btn ghost danger", onClick: () => { h.imprints.splice(i, 1); onChange(); renderImp(); } }, "Remove imprint"));
      impRoot.appendChild(card);
    });
    impRoot.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { h.imprints.push({ label: "", name: "", description: "", background: "", pills: [] }); onChange(); renderImp(); } }, "+ Add imprint"));
  }
  renderImp();
  imp.appendChild(impRoot);
  root.appendChild(imp);

  const par = el("fieldset", { class: "fieldset" }, el("legend", {}, "Parallax bands"));
  par.appendChild(field("Top band image", imageInput(h.parallax_top, v => { h.parallax_top = v; onChange(); }, "hero")));
  par.appendChild(field("Bottom band image", imageInput(h.parallax_bottom, v => { h.parallax_bottom = v; onChange(); }, "hero")));
  root.appendChild(par);

  const ec = el("fieldset", { class: "fieldset" }, el("legend", {}, "Event card (home)"));
  ec.appendChild(field("Kicker", textInput(h.event_card.kicker, v => { h.event_card.kicker = v; onChange(); })));
  ec.appendChild(field("Title", textInput(h.event_card.title, v => { h.event_card.title = v; onChange(); })));
  ec.appendChild(field("Meta (HTML)", textarea(h.event_card.meta_html, v => { h.event_card.meta_html = v; onChange(); }, { rows: 3 })));
  root.appendChild(ec);

  const ft = el("fieldset", { class: "fieldset" }, el("legend", {}, "Featured transmission card"));
  ft.appendChild(field("Card title", textInput(h.featured_track.title, v => { h.featured_track.title = v; onChange(); })));
  ft.appendChild(field("Card meta (HTML allowed)", textarea(h.featured_track.meta, v => { h.featured_track.meta = v; onChange(); }, { rows: 1 })));
  ft.appendChild(field("URL", textInput(h.featured_track.url, v => { h.featured_track.url = v; onChange(); })));
  root.appendChild(ft);

  const rp = el("fieldset", { class: "fieldset" }, el("legend", {}, "Roster preview list"));
  const rpRoot = el("div");
  function renderRp(): void {
    rpRoot.innerHTML = "";
    h.roster_preview.forEach((p, i: number) => {
      rpRoot.appendChild(el("div", { class: "row2", style: "margin-bottom:6px" },
        el("input", { type: "text", value: p.name, placeholder: "DJ name", onInput: (e: Event) => { p.name = (e.target as HTMLInputElement).value; onChange(); } }),
        el("div", { style: "display:flex;gap:6px" },
          el("input", { type: "text", value: p.summary, placeholder: "Genre summary", onInput: (e: Event) => { p.summary = (e.target as HTMLInputElement).value; onChange(); } }),
          el("button", { type: "button", class: "btn ghost danger", onClick: () => { h.roster_preview.splice(i, 1); onChange(); renderRp(); } }, "×")
        )
      ));
    });
    rpRoot.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { h.roster_preview.push({ name: "", summary: "" }); onChange(); renderRp(); } }, "+ Add row"));
  }
  renderRp();
  rp.appendChild(rpRoot);
  root.appendChild(rp);

  const fs = el("fieldset", { class: "fieldset" }, el("legend", {}, "Featured sets"));
  const fsRoot = el("div");
  function renderFs(): void {
    fsRoot.innerHTML = "";
    h.featured_sets.forEach((s: FeaturedSet, i: number) => {
      const card = el("div", { style: "border:1px solid var(--border);padding:10px;margin-bottom:8px;border-radius:5px" });
      card.appendChild(field("DJ", textInput(s.dj, v => { s.dj = v; onChange(); })));
      card.appendChild(field("Context", textInput(s.context, v => { s.context = v; onChange(); })));
      const ps = el("select", {
        onChange: (e: Event) => { s.platform = (e.target as HTMLSelectElement).value as FeaturedSet["platform"]; onChange(); renderFs(); }
      },
        el("option", { value: "soundcloud" }, "SoundCloud"),
        el("option", { value: "mixcloud" }, "Mixcloud")
      ) as HTMLSelectElement;
      ps.value = s.platform;
      card.appendChild(field("Platform", ps));
      if (s.platform === "soundcloud") card.appendChild(field("URL", textInput(s.url ?? "", v => { s.url = v; onChange(); })));
      else card.appendChild(field("Mixcloud feed", textInput(s.feed ?? "", v => { s.feed = v; onChange(); })));
      card.appendChild(el("button", { type: "button", class: "btn ghost danger", onClick: () => { h.featured_sets.splice(i, 1); onChange(); renderFs(); } }, "Remove set"));
      fsRoot.appendChild(card);
    });
    fsRoot.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => { h.featured_sets.push({ dj: "", context: "", platform: "soundcloud", url: "" }); onChange(); renderFs(); } }, "+ Add featured set"));
  }
  renderFs();
  fs.appendChild(fsRoot);
  root.appendChild(fs);

  const ls = el("fieldset", { class: "fieldset" }, el("legend", {}, "Footer split: label statement + location"));
  ls.appendChild(field("Label statement title", textInput(h.label_statement.title, v => { h.label_statement.title = v; onChange(); })));
  ls.appendChild(field("Label statement body", textarea(h.label_statement.body, v => { h.label_statement.body = v; onChange(); }, { rows: 3 })));
  ls.appendChild(field("Location info title", textInput(h.location_info.title, v => { h.location_info.title = v; onChange(); })));
  ls.appendChild(field("Location info body", textarea(h.location_info.body, v => { h.location_info.body = v; onChange(); }, { rows: 3 })));
  root.appendChild(ls);
}

// ---- Page form (about / contact / demos / releases / not_found) ----
//
// Static pages still use a JSON-only editor — the structure of each varies
// enough that bespoke forms would be a lot of code for low edit volume.

export type PageKey = keyof PagesContent;

export interface PageFormOpts {
  onChange: () => void;
  heading?: string;
}

export function renderPageForm(key: PageKey, pages: PagesContent, root: HTMLElement, opts: PageFormOpts): void {
  const onChange = opts.onChange;
  root.innerHTML = "";
  const label = key === "not_found" ? "404 page" : key;
  if (opts.heading !== "") root.appendChild(el("h3", {}, opts.heading ?? `Edit ${label}`));

  const fs = el("fieldset", { class: "fieldset" }, el("legend", {}, label));
  fs.appendChild(field(
    "(JSON · advanced)",
    textarea(JSON.stringify(pages[key], null, 2), v => {
      try {
        (pages as unknown as Record<string, unknown>)[key] = JSON.parse(v);
        onChange();
      } catch {
        // ignore until valid JSON
      }
    }, { rows: 18 }),
    "Edit the raw JSON for this page. Save to apply (must be valid JSON)."
  ));
  root.appendChild(fs);
}

// ---- Release ----

export function createNewRelease(slug: string): Release {
  return {
    slug,
    title: slug,
    artist: "",
    release_date: "",
    cover_image: null,
    embed_platform: null,
    embed_url: "",
    embed_feed: "",
    body_html: "",
    tags: [],
    page_meta: { description: `${slug} on Breathe Music Records.` },
    published: false,
  };
}

export interface ReleaseFormOpts {
  onChange: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
  heading?: string;
}

export function renderReleaseForm(rel: Release, root: HTMLElement, opts: ReleaseFormOpts): void {
  const onChange = opts.onChange;
  const showHeading = opts.heading !== "";
  const showDelete = opts.showDelete ?? !!opts.onDelete;
  root.innerHTML = "";

  if (showHeading) {
    const h = el("h3", {}, opts.heading ?? rel.title ?? "(untitled)");
    if (showDelete && opts.onDelete) {
      const onDelete = opts.onDelete;
      h.appendChild(el("button", {
        type: "button", class: "btn ghost danger", style: "float:right;font-size:12px",
        onClick: () => {
          if (!confirm(`Delete ${rel.title}? Saved on next press.`)) return;
          onDelete();
        }
      }, "Delete"));
    }
    root.appendChild(h);
  }

  root.appendChild(field("Title", textInput(rel.title, v => { rel.title = v; onChange(); })));
  root.appendChild(field("Artist", textInput(rel.artist, v => { rel.artist = v; onChange(); })));
  root.appendChild(field("Slug (URL)", textInput(rel.slug, v => { rel.slug = v; onChange(); }), "Lowercase, hyphens. URL: /releases/<slug>/"));
  root.appendChild(field("Release date", textInput(rel.release_date, v => { rel.release_date = v; onChange(); }, { placeholder: "YYYY-MM-DD" })));
  root.appendChild(field("Cover image", imageInput(rel.cover_image ?? "", v => { rel.cover_image = v || null; onChange(); }, "releases")));
  root.appendChild(field("Tags", tagInput(rel.tags, v => { rel.tags = v; onChange(); })));

  // Embed
  const em = el("fieldset", { class: "fieldset" }, el("legend", {}, "Embed / streaming link"));
  const platSel = el("select", {
    onChange: (e: Event) => {
      const v = (e.target as HTMLSelectElement).value;
      rel.embed_platform = (v || null) as ReleasePlatform | null;
      onChange();
      renderReleaseForm(rel, root, opts);
    }
  },
    el("option", { value: "" }, "(none)"),
    el("option", { value: "soundcloud" }, "SoundCloud"),
    el("option", { value: "mixcloud" }, "Mixcloud"),
    el("option", { value: "bandcamp" }, "Bandcamp"),
    el("option", { value: "spotify" }, "Spotify"),
    el("option", { value: "youtube" }, "YouTube"),
  ) as HTMLSelectElement;
  platSel.value = rel.embed_platform ?? "";
  em.appendChild(field("Platform", platSel));
  if (rel.embed_platform) {
    em.appendChild(field("Click-through URL", textInput(rel.embed_url, v => { rel.embed_url = v; onChange(); }, { type: "url" })));
    if (rel.embed_platform === "mixcloud") {
      em.appendChild(field("Mixcloud feed", textInput(rel.embed_feed, v => { rel.embed_feed = v; onChange(); }), "Path portion only, e.g. /user/mix/"));
    }
  }
  root.appendChild(em);

  root.appendChild(field("Body (HTML)", textarea(rel.body_html, v => { rel.body_html = v; onChange(); }, { rows: 8 }), "Long-form description. Plain HTML."));

  root.appendChild(field("Page meta description", textarea(rel.page_meta.description, v => { rel.page_meta.description = v; onChange(); }, { rows: 2 })));

  // Published
  const pubBox = el("input", { type: "checkbox" }) as HTMLInputElement;
  pubBox.checked = !!rel.published;
  pubBox.addEventListener("change", () => { rel.published = pubBox.checked; onChange(); });
  root.appendChild(field("Published (visible on public site)", pubBox));
}

// ---- Post ----

export function createNewPost(slug: string): Post {
  return {
    slug,
    title: slug,
    excerpt: "",
    body_md: "",
    body_html: "",
    author: "",
    cover_image: null,
    tags: [],
    page_meta: { description: "" },
    published_at: "",
  };
}

export interface PostFormOpts {
  onChange: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
  heading?: string;
}

export function renderPostForm(post: Post, root: HTMLElement, opts: PostFormOpts): void {
  const onChange = opts.onChange;
  const showHeading = opts.heading !== "";
  const showDelete = opts.showDelete ?? !!opts.onDelete;
  root.innerHTML = "";

  if (showHeading) {
    const h = el("h3", {}, opts.heading ?? post.title ?? "(untitled)");
    if (showDelete && opts.onDelete) {
      const onDelete = opts.onDelete;
      h.appendChild(el("button", {
        type: "button", class: "btn ghost danger", style: "float:right;font-size:12px",
        onClick: () => {
          if (!confirm(`Delete ${post.title}?`)) return;
          onDelete();
        }
      }, "Delete"));
    }
    root.appendChild(h);
  }

  root.appendChild(field("Title", textInput(post.title, v => { post.title = v; onChange(); })));
  root.appendChild(field("Slug (URL)", textInput(post.slug, v => { post.slug = v; onChange(); }), "Lowercase, hyphens. URL: /posts/<slug>/"));
  root.appendChild(field("Author", textInput(post.author, v => { post.author = v; onChange(); })));
  root.appendChild(field("Excerpt", textarea(post.excerpt, v => { post.excerpt = v; onChange(); }, { rows: 2 }), "One-line summary for index card + meta."));
  root.appendChild(field("Cover image", imageInput(post.cover_image ?? "", v => { post.cover_image = v || null; onChange(); }, "posts"), "Used as hero on detail page and card thumbnail on index."));
  root.appendChild(field("Tags", tagInput(post.tags, v => { post.tags = v; onChange(); })));
  root.appendChild(field("Body (Markdown)", textarea(post.body_md, v => { post.body_md = v; onChange(); }, { rows: 18 }), "GitHub-flavored markdown. HTML is rendered server-side on save."));
  root.appendChild(field("Page meta description", textarea(post.page_meta.description, v => { post.page_meta.description = v; onChange(); }, { rows: 2 })));
  root.appendChild(field(
    "Publish date (ISO timestamp; blank = draft)",
    textInput(post.published_at, v => { post.published_at = v; onChange(); }, { placeholder: "2026-05-11T12:00:00Z" }),
    "Drafts are hidden from /posts/ and 404 if requested directly."
  ));
}
